import { mdiAlertCircleOutline } from '@mdi/js'
import CardList from 'components/CardList'
import EmptyView from 'components/EmptyView'
import FileCard, { FileUploadCTACard } from 'components/FileCard'
import { ConnectedFileInput } from 'components/FileHelpers'
import { ALL_FILTER, FileFolder, MyFile } from 'interfaces'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { selectedContentItemsSelector } from 'services/content/selectors'
import { setSorting } from 'services/uploads'
import { librarySortingSelector } from 'services/uploads/selectors'
import FileFolderViewHeader from '../FileFolderViewHeader'
import styles from './FileFolderCarousel.pcss'
import { ContentType } from 'shared'

const MY_LIB_URL = '/content/library'
const PAGE_SIZE = 20

export interface FileFolderCarouselProps {
  folder: FileFolder
  navPrefix?: string
  withEmptyViewCTA?: boolean
  withCTACard?: boolean
  hideHeader?: boolean
  staticHeader?: boolean
  filter?: ContentType
  title?: React.ReactNode
  className?: string
  onCreatePost: (file: MyFile) => void
  onCardClick?: (file: MyFile) => void
  onSeeAll?: (folderId: string) => void
  navigate?: (url: string) => void
}

export function FileFolderCarousel(props: FileFolderCarouselProps) {
  const intl = useIntl()
  const dispatch = useDispatch()
  const folder = props.folder
  const filters = folder.filters
  const selectedItems = useSelector(selectedContentItemsSelector)
  const sorting = useSelector(librarySortingSelector)
  const folderSorting = sorting[folder.id]
  const defaultFilter = filters.length !== 1 ? ALL_FILTER : folder.filters[0]
  const controlled = props.filter !== undefined
  const [internalFilter, setInternalFilter] = React.useState(defaultFilter)
  const filter = controlled ? props.filter as string : internalFilter
  const setFilter = controlled ? () => undefined : setInternalFilter
  const [page, setPage] = React.useState(1)

  const visibleFiles = React.useMemo(() => {
    const filtered = folder
      ? filter === ALL_FILTER
        ? Object.values(folder.files)
        : Object.values(folder.files).filter(f => f.type === filter)
      : []

    const sorted = filtered.sort((f1: MyFile, f2: MyFile) => {
      // sort files based on sortBy and sortDirection
      if (folderSorting?.sortBy === 'name') {
        return folderSorting?.sortDirection === 'asc'
          ? f2.name.localeCompare(f1.name)
          : f1.name.localeCompare(f2.name)
      }
      return folderSorting?.sortDirection === 'asc'
        ? f1.createdAt.localeCompare(f2.createdAt)
        : f2.createdAt.localeCompare(f1.createdAt)
    })
    return sorted
  }, [filter, folder, folderSorting?.sortBy, folderSorting?.sortDirection])

  const visibleFilesEndIndex = page * PAGE_SIZE

  const onRequestNextPage = () => {
    if (page * PAGE_SIZE < visibleFiles.length) {
      setPage(page + 1)
    }
  }

  const navigateToMyLibrary = () => {
    if (props.navigate) {
      props.navigate(MY_LIB_URL)
    }
  }

  const onSortChange = (sortBy: 'name' | 'date', sortDirection: 'asc' | 'desc') => {
    dispatch(setSorting({ folderId: folder.id, sortBy, sortDirection }))
  }

  if (!controlled && filter !== defaultFilter && !filters.includes(filter as ContentType)) {
    setFilter(defaultFilter)
  }

  const isEmptyFolder = Object.keys(props.folder.files).length === 0
  const showEmptyCTA = isEmptyFolder && props.withEmptyViewCTA

  return (
    <div className={`${styles.container} ${props.className || ''}`} data-testid="file-folder-carousel">
      {
        !props.hideHeader && (
          <FileFolderViewHeader
            folderId={folder.id}
            title={props.title}
            folderName={folder.name}
            navPrefix={props.navPrefix}
            activeFilter={filter}
            filters={filters}
            hideTypeFilter={controlled}
            staticHeader={props.staticHeader}
            onSeeAll={props.onSeeAll}
            sorting={{
              sortBy: folderSorting?.sortBy || 'date',
              sortDirection: folderSorting?.sortDirection || 'desc',
              onChange: onSortChange
            }}
            actionsDisabled={isEmptyFolder}
            onFilterChange={setFilter}
          />
        )
      }
      <div className={`${styles['list-box']} ${showEmptyCTA ? styles['with-cta'] : ''}`}>
        {
          visibleFiles.length > 0 ? (
            <CardList
              containerClassName={styles.list}
              skipCardHoverCheck
              onScrollLimit={onRequestNextPage}
            >
              {
                visibleFiles.slice(0, visibleFilesEndIndex).map(f => (
                  <FileCard
                    small
                    file={f}
                    selected={Boolean(selectedItems[f.id])}
                    key={f.id}
                    onClick={props.onCardClick}
                    onCreatePost={props.onCreatePost}
                  />
                ))
              }
              {props.withCTACard && <FileUploadCTACard small />}
            </CardList>
          ) : !(props.withEmptyViewCTA && isEmptyFolder) && (
            <EmptyView
              title={intl.formatMessage({ id: `uploads.error.carousel-no-${filter}` })}
              icon={mdiAlertCircleOutline}
              carousel
            />
          )
        }
        {
          showEmptyCTA && (
            <EmptyView
              title={intl.formatMessage({ id: `uploads.error.empty-view-carousel-title` })}
              subtitle={(
                <ConnectedFileInput
                  key="file-input"
                  openDialogOnChange
                  onChange={navigateToMyLibrary}
                >
                  <div className={styles['upload-link']} key="link">
                    <FormattedMessage id="uploads.upload-content-cta" key="message" />
                  </div>
                </ConnectedFileInput>
              )}
              icon={mdiAlertCircleOutline}
              carousel
            />
          )
        }
      </div>
    </div>
  )
}

export default FileFolderCarousel
